import {
  useFloating,
  autoUpdate,
  offset,
  useInteractions,
  useDismiss,
  useClick,
} from "@floating-ui/react";
import { useState } from "react";
import { ChromePicker } from "react-color";
import { Controller, useFormContext } from "react-hook-form";

import { loadLocale } from "@smart/itops-locale-dom";
import { Button } from "@smart/itops-sb-design-system-dom";
import { Gql } from "@smart/manage-gql-operations-dom";

type BrandingInput = NonNullable<
  Gql.UpdateSettingMutationVariables["fields"]["branding"]
>;

export const BrandColor = () => {
  const { control } = useFormContext<BrandingInput>();
  const locale = loadLocale();

  return (
    <div className="flex flex-col gap-medium">
      <div className="flex flex-col gap-small">
        <h3 className="text-paragraph font-semibold">
          Brand {locale.terms.colour.header}
        </h3>
        <p className="text-caption text-neutral-600">
          Define {locale.terms.colour.name} to {locale.terms.customise.future}{" "}
          the display of your Intake Forms.
        </p>
      </div>
      <Controller
        control={control}
        name="brandColor"
        render={({ field }) => {
          const colorValue = field.value || undefined;
          const [isOpen, setIsOpen] = useState(false);

          const { refs, floatingStyles, context } = useFloating({
            open: isOpen,
            onOpenChange: setIsOpen,
            placement: "bottom-start",
            middleware: [offset(8)],
            whileElementsMounted: autoUpdate,
          });

          const { getReferenceProps, getFloatingProps } = useInteractions([
            useClick(context),
            useDismiss(context),
          ]);

          const handleColorChange = (color: { hex: string }) => {
            field.onChange(color.hex);
          };

          return (
            <>
              <div ref={refs.setReference} {...getReferenceProps()}>
                {colorValue ? (
                  <div
                    className="w-[4rem] h-[4rem] rounded cursor-pointer"
                    style={{
                      backgroundColor: colorValue,
                    }}
                    aria-label={`Brand ${locale.terms.colour.name} picker`}
                  />
                ) : (
                  <Button
                    leftIcon="regularPlus"
                    variant="secondaryDefault"
                    aria-label={`Add brand ${locale.terms.colour.name}`}
                  />
                )}
              </div>
              {isOpen && (
                <div
                  ref={refs.setFloating}
                  style={floatingStyles}
                  {...getFloatingProps()}
                  className="z-modal"
                >
                  <ChromePicker
                    color={colorValue}
                    onChange={handleColorChange}
                  />
                </div>
              )}
            </>
          );
        }}
      />
    </div>
  );
};
